import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  BarChart3,
  LineChart,
  AlertTriangle,
  Search,
  TrendingUp,
  Calculator,
  Gamepad2
} from 'lucide-react';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Stock Picking: How to Evaluate and Choose Stocks",
  "description": "Learn stock picking fundamentals, from fundamental and technical analysis to common mistakes, and train your analysis skills with Wallstreetle.",
  "url": "https://wallstreetle.com/blog/stock-picking",
  "datePublished": "2026-03-20",
  "dateModified": "2026-03-20",
  "author": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "publisher": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "image": "https://wallstreetle.com/assets/share-link-image.png"
};

export default function StockPickingPage() {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | Stock Picking</title>
        <meta
          name="description"
          content="Learn how to pick stocks using fundamental and technical analysis. Avoid common mistakes and sharpen your stock analysis skills with Wallstreetle's daily puzzles."
        />
        <meta
          name="keywords"
          content="stock picking, how to pick stocks, stock analysis, fundamental analysis, technical analysis, stock evaluation, value investing, stock screening, stock pick, best stocks to buy"
        />
        <link rel="canonical" href="https://wallstreetle.com/blog/stock-picking" />

        <meta property="og:title" content="Wallstreetle | Stock Picking" />
        <meta property="og:description" content="Learn stock picking with fundamental and technical analysis. Practice with Wallstreetle's daily puzzles." />
        <meta property="og:url" content="https://wallstreetle.com/blog/stock-picking" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | Stock Picking" />
        <meta property="twitter:description" content="Learn stock picking and practice analysis skills with free daily stock puzzles." />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <BarChart3 className="w-4 h-4" /> Stock Analysis
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
            Stock Picking
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            How to evaluate companies, avoid common traps, and develop the analytical skills that separate successful investors from the crowd.
          </p>
        </div>

        {/* Section: Fundamentals vs Technicals */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Two lenses for evaluating stocks</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                <Search className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Fundamental Analysis</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Looks at a company's financial health and business quality. You examine earnings, revenue growth, profit margins, debt levels, and competitive position.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Best for:</strong> Long-term investors who want to buy quality companies at fair prices.
              </p>
            </Card>
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400">
                <LineChart className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Technical Analysis</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Studies price charts and trading patterns. You look at trends, support/resistance levels, volume, and momentum indicators.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Best for:</strong> Traders looking for entry and exit points based on price action.
              </p>
            </Card>
          </div>
        </section>

        {/* Section: Key Metrics */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Key metrics to evaluate a stock</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: 'Revenue Growth', desc: 'Is the company growing its top line? Look for consistent year-over-year increases rather than one-off spikes.', icon: TrendingUp, color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' },
              { title: 'Profit Margins', desc: 'High and expanding margins signal pricing power and operational efficiency. Compare against industry peers.', icon: Calculator, color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' },
              { title: 'P/E Ratio', desc: 'Price relative to earnings. Low P/E can mean undervalued, or it could signal a declining business. Always consider context.', icon: BarChart3, color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' },
              { title: 'Debt-to-Equity', desc: 'How leveraged is the company? Moderate debt is fine; excessive debt becomes dangerous in downturns.', icon: AlertTriangle, color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' },
              { title: 'Free Cash Flow', desc: 'Cash generated after capital expenditures. Positive free cash flow means a company can fund growth, dividends, or buybacks.', icon: LineChart, color: 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400' },
              { title: 'Market Position', desc: 'Is the company a leader in its industry? Strong competitive moats (brand, network effects, switching costs) protect long-term returns.', icon: Search, color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="p-5 hover:shadow-lg transition-shadow">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${item.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Section: Common Mistakes */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Common stock picking mistakes</h2>
          <Card className="p-6">
            <ul className="space-y-4">
              {[
                { mistake: 'Chasing hype', fix: 'By the time a stock is trending on social media, the easy gains are often gone. Do your own research.' },
                { mistake: 'Ignoring valuation', fix: 'A great company at a terrible price is a bad investment. Always check what you\'re paying relative to earnings and growth.' },
                { mistake: 'No diversification', fix: 'Putting everything in one stock is gambling, not investing. Spread across sectors and company sizes.' },
                { mistake: 'Emotional trading', fix: 'Panic selling in downturns and FOMO buying in rallies destroy returns. Have a plan and stick to it.' },
                { mistake: 'Skipping the financials', fix: 'Buying a stock because you like the product isn\'t analysis. Always look at the numbers.' },
              ].map((item) => (
                <li key={item.mistake} className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">{item.mistake}:</span>{' '}
                    <span className="text-muted-foreground">{item.fix}</span>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </section>

        {/* Section: How Wallstreetle Trains Your Skills */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Train your stock analysis skills</h2>
          <Card className="p-6 sm:p-8 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-orange-200 dark:border-orange-800">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/50 rounded-lg shrink-0">
                <Gamepad2 className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Wallstreetle is essentially a stock picking trainer disguised as a game. Every puzzle gives you a price chart and progressively reveals financial metrics, the same data you'd analyze when evaluating a real stock.
                </p>
                <p>
                  With <strong className="text-foreground">9 categories</strong> spanning tech, pharmaceuticals, energy, consumer goods, and more, you'll build familiarity with different industry profiles. You'll learn why tech companies have high margins, why energy stocks are cyclical, and what makes a financial services company tick.
                </p>
                <p>
                  After hundreds of puzzles, evaluating a new stock won't feel like a guessing game. It'll feel like pattern recognition.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">
            Sharpen your stock picking skills
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Analyze real financial data every day and train the pattern recognition that great investors rely on.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            Play Wallstreetle Free
          </Link>
        </Card>
      </div>
    </>
  );
}
